import { setBookingPagePublishedStatus } from '../actions/setBookingPagePublishedStatus';
import type { PersonalBookingData } from 'bookwithme-shared';
import {
    getBookingSessionData,
    getObscuredCurrentUserAddress,
    ORGANIZER_CHECK_ISPUBLISHED_ENTRYPOINT,
    ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES,
} from 'bookwithme-shared';
import setMailboxPuid from '../actions/setMailboxPuid';
import { PublishStatus } from '../store/schema/BookWithMeOrganizerStore';
import { logUsage } from 'owa-analytics';
import getMeetingTypeExtension from '../services/getMeetingTypeExtension';
import type { PublishingSettingsExtension } from '../store/schema/PublishingSettingsExtension';
import type { OwaDate } from 'owa-datetime';
import { differenceInMilliseconds, differenceInMinutes, owaDate } from 'owa-datetime';
import setIsPublishedForAttendee from './setIsPublishedForAttendee';
import getEntryPoint from '../selectors/getEntryPoint';
import { PUBLISHING_SETTINGS_ID } from '../selectors/getPublishSettingsVariables';
import { getUserMailboxGuid } from '../utils/getMailboxGuid';
import getMailboxPuid from '../selectors/getMailboxPuid';
import setCheckBookingPagePublishedIsCalled from '../actions/setCheckBookingPagePublishedIsCalled';
import store from '../store/Store';
import { isFeatureEnabled } from 'owa-feature-flags';

import getBookingBusiness from 'bookings-shared/lib/c1/services/getBookingBusiness';
import { getUserConfiguration } from 'owa-session-store';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';
import { setPbEnabledStatus } from '../actions/pbEnabledStatusActions';
import type ServicesRequestStatus from '../store/schema/ServicesRequestStatus';
import mapBookingBusinessEntryToPublishingSettings from 'bookings-shared/lib/c1/mappers/mapBookingBusinessEntryToPublishingSettings';

var tries = 1;
const maxTries = ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES;
export default async function checkBookingPageIsPublished() {
    // Check to ensure this loop hasn't been started in a different entry-point
    if (store.checkBookingPagePublishedIsCalled) {
        return;
    }
    const userMailbox = getObscuredCurrentUserAddress();
    setBookingPagePublishedStatus(PublishStatus.Publishing);
    setCheckBookingPagePublishedIsCalled(true);

    //we will be calling getBussiness API instead of eventify to get isPublished status
    if (isFeatureEnabled('bookwithme-c1B2Migration')) {
        const bookingsSMTPAddress = getUserConfiguration().SessionSettings?.UserPrincipalName || '';
        const response = await getBookingBusiness(bookingsSMTPAddress, 'BookWithMe');
        if (!response.hasError && response.businessInfo) {
            const mappedResponse = mapBookingBusinessEntryToPublishingSettings(
                response.businessInfo,
                'BookWithMe'
            );
            store.pbpPublishingSetting.publishingSettings = mappedResponse;

            if (response.businessInfo.isPublished) {
                setBookingPagePublishedStatus(PublishStatus.Complete);
                //setIsPublishedForAttendee(); No need to set this flag for attendee as we are not using eventify
            } else {
                setBookingPagePublishedStatus(PublishStatus.Incomplete);
            }
        } else {
            setPbEnabledStatus(4);
        }
    } else {
        const TIMEOUT = 2000;
        const publishPageCheck = window.setInterval(async () => {
            if (userMailbox) {
                /*
                 * Use ORGANIZER_CHECK_ISPUBLISHED as entry point in the call to getBookingSessionData, to avoid unnecessary error logs.
                 * When the call is from the checkBookingPageIsPublished, while validating IsPublished replication completed in AD,
                 * don't want to log errors, to avoid false positive escalations.
                 */
                await getBookingSessionData(
                    userMailbox /* bookingMailbox */,
                    ORGANIZER_CHECK_ISPUBLISHED_ENTRYPOINT /* entryPoint */,
                    tries /* checkIsPublishedCount */
                ).then(async (response: PersonalBookingData | null) => {
                    if (response === undefined || response === null) {
                        if (tries >= maxTries) {
                            setBookingPagePublishedStatus(PublishStatus.Incomplete);
                            clearInterval(publishPageCheck);
                        }
                        tries += 1;
                    } else {
                        setMailboxPuid(response.BookingMailboxPuid ?? '');
                        const mailboxHash = response.BookingMailboxHash;
                        if (mailboxHash) {
                            setBookingPagePublishedStatus(PublishStatus.Complete);
                            setIsPublishedForAttendee();
                            let diffInMin = null;
                            let diffInMs = null;
                            const result = await getMeetingTypeExtension(PUBLISHING_SETTINGS_ID);
                            if (result?.additionalInformation) {
                                const publishingSettingsExtension: PublishingSettingsExtension =
                                    JSON.parse(result.additionalInformation);
                                const nowUtc = owaDate();
                                const whenSettingsCreatedUtc: OwaDate = owaDate(
                                    'UTC',
                                    publishingSettingsExtension.whenSettingsCreated
                                );
                                diffInMin = differenceInMinutes(nowUtc, whenSettingsCreatedUtc);
                                diffInMs = differenceInMilliseconds(nowUtc, whenSettingsCreatedUtc);
                            }
                            logUsage('onFirstTimeUserPublishBookingPage', {
                                publishStatus: 'Complete',
                                entryPoint: getEntryPoint(),
                                timeInMinutes: !result?.additionalInformation ? 'NA' : diffInMin,
                                timeInMilliseconds: !result?.additionalInformation
                                    ? 'NA'
                                    : diffInMs,
                                mailboxGuid: getUserMailboxGuid(),
                                bookingMailboxPuid: getMailboxPuid(),
                            });
                            clearInterval(publishPageCheck);
                        }
                    }
                });
            }
        }, TIMEOUT);
    }
}
